import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  DeviceCommandTypeSchema,
  type DeviceCommandHistoryItem,
  type DeviceCommandType,
} from '@ghostscreen/contracts';
import { QueryEmptyState } from '../../components/dashboard/QueryEmptyState';
import { QueryErrorState } from '../../components/dashboard/QueryErrorState';
import { QueryLoadingBlock } from '../../components/dashboard/QueryLoadingBlock';
import { i18n } from '../../i18n';
import { api } from '../../lib/api';
import { formatRelativeTime } from '../../lib/dashboard/relativeTime';
import { useQueryRetry } from '../../lib/dashboard/useQueryRetry';

type DeviceCommandHistoryProps = {
  organisationId: string;
  deviceId: string;
};

const PAGE_SIZE = 25;

const STATUS_OPTIONS = ['pending', 'sent', 'acknowledged', 'executed', 'failed', 'expired'] as const;
type StatusOption = (typeof STATUS_OPTIONS)[number];

function isStatusOption(status: string): status is StatusOption {
  return (STATUS_OPTIONS as readonly string[]).includes(status);
}

function statusLabel(status: string): string {
  return isStatusOption(status) ? i18n.t(`displays:fleet.history.statuses.${status}`) : status;
}

// Text label always accompanies the dot — status must read without colour.
const STATUS_TONE: Record<string, string> = {
  pending: 'text-muted',
  sent: 'text-muted',
  acknowledged: 'text-muted',
  executed: 'text-ok',
  failed: 'text-bad',
  expired: 'text-amber-600',
};

const STATUS_DOT: Record<string, string> = {
  pending: 'bg-muted',
  sent: 'bg-muted',
  acknowledged: 'bg-muted',
  executed: 'bg-ok',
  failed: 'bg-bad',
  expired: 'bg-amber-600',
};

// Full wire type enum -- includes reserved types no longer operator-enqueueable,
// which may still exist in history and must remain filterable.
const TYPE_OPTIONS = DeviceCommandTypeSchema.options;

const TYPE_LABEL_KEY = {
  SYNC_NOW: 'fleet.commands.syncNow',
  CLEAR_CACHE: 'fleet.commands.clearCache',
  RESTART_PLAYER: 'fleet.commands.restartPlayer',
  REFRESH_CONTENT: 'fleet.commands.refreshContent',
  REQUEST_SCREENSHOT: 'fleet.commands.requestScreenshot',
  COLLECT_LOGS: 'fleet.commands.collectLogs',
} as const satisfies Record<DeviceCommandType, string>;

function typeLabel(type: string): string {
  return type in TYPE_LABEL_KEY
    ? i18n.t(`displays:${TYPE_LABEL_KEY[type as DeviceCommandType]}`)
    : type;
}

function completionLabel(item: DeviceCommandHistoryItem): string | null {
  if (item.status === 'executed' || item.status === 'failed') {
    return item.acknowledgedAt ? formatRelativeTime(item.acknowledgedAt) : null;
  }
  if (item.status === 'expired') {
    return item.expiresAt
      ? i18n.t('displays:fleet.history.expiredLimit', { time: formatRelativeTime(item.expiresAt) })
      : i18n.t('displays:fleet.history.expired');
  }
  return null;
}

/**
 * Tenant-scoped, filterable, paginated command history for one device.
 * Filters are part of the query key, so changing either one discards
 * accumulated pages and starts a fresh cursor -- never a stale mix.
 */
export function DeviceCommandHistory({ organisationId, deviceId }: DeviceCommandHistoryProps) {
  const { t } = useTranslation(['displays', 'common']);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<string | null>(null);

  const historyQuery = useInfiniteQuery({
    queryKey: ['device-command-history', organisationId, deviceId, statusFilter, typeFilter],
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      api.listDeviceCommandHistory(organisationId, deviceId, {
        ...(statusFilter ? { status: statusFilter } : {}),
        ...(typeFilter ? { type: typeFilter } : {}),
        ...(pageParam ? { cursor: pageParam } : {}),
        limit: PAGE_SIZE,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });

  const { isRetrying, retry } = useQueryRetry(() => historyQuery.refetch());

  const items = historyQuery.data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-xs font-semibold text-muted">
          {t('fleet.history.status')}
          <select
            value={statusFilter ?? ''}
            onChange={(event) => setStatusFilter(event.target.value || null)}
            className="mt-1 block min-w-[160px] rounded-lg border border-line bg-field px-3 py-2 text-sm text-ink"
          >
            <option value="">{t('fleet.history.allStatuses')}</option>
            {STATUS_OPTIONS.map((status) => (
              <option key={status} value={status}>
                {statusLabel(status)}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs font-semibold text-muted">
          {t('fleet.history.commandType')}
          <select
            value={typeFilter ?? ''}
            onChange={(event) => setTypeFilter(event.target.value || null)}
            className="mt-1 block min-w-[200px] rounded-lg border border-line bg-field px-3 py-2 text-sm text-ink"
          >
            <option value="">{t('fleet.history.allTypes')}</option>
            {TYPE_OPTIONS.map((type) => (
              <option key={type} value={type}>
                {typeLabel(type)}
              </option>
            ))}
          </select>
        </label>
      </div>

      {historyQuery.isError ? (
        <QueryErrorState
          message={t('fleet.history.loadError')}
          onRetry={retry}
          isRetrying={isRetrying}
        />
      ) : historyQuery.isPending ? (
        <QueryLoadingBlock variant="rows" />
      ) : items.length === 0 ? (
        <QueryEmptyState>
          {statusFilter || typeFilter ? t('fleet.history.emptyFiltered') : t('fleet.history.empty')}
        </QueryEmptyState>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-line bg-panel">
          <table className="w-full text-start text-sm">
            <thead className="border-b border-line text-xs uppercase tracking-wide text-muted">
              <tr>
                <th className="px-4 py-3">{t('fleet.history.columns.command')}</th>
                <th className="px-4 py-3">{t('fleet.history.columns.status')}</th>
                <th className="px-4 py-3">{t('fleet.history.columns.issued')}</th>
                <th className="px-4 py-3">{t('fleet.history.columns.issuedBy')}</th>
                <th className="px-4 py-3">{t('fleet.history.columns.sent')}</th>
                <th className="px-4 py-3">{t('fleet.history.columns.completed')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} className="border-b border-line last:border-0">
                  <td className="px-4 py-3 text-ink">{typeLabel(item.type)}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center gap-1.5 text-xs font-semibold ${STATUS_TONE[item.status] ?? 'text-muted'}`}
                    >
                      <span className={`h-1.5 w-1.5 rounded-full ${STATUS_DOT[item.status] ?? 'bg-muted'}`} />
                      {statusLabel(item.status)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-muted">{formatRelativeTime(item.createdAt)}</td>
                  <td className="px-4 py-3 text-muted">
                    {item.issuedByDisplayName ?? t('fleet.history.unknownOperator')}
                  </td>
                  <td className="px-4 py-3 text-muted">
                    {item.sentAt ? formatRelativeTime(item.sentAt) : '—'}
                  </td>
                  <td className="px-4 py-3 text-muted">
                    {item.resultMessage ? (
                      <span className="text-bad">{item.resultMessage}</span>
                    ) : (
                      (completionLabel(item) ?? '—')
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {historyQuery.hasNextPage && (
        <button
          type="button"
          onClick={() => historyQuery.fetchNextPage()}
          disabled={historyQuery.isFetchingNextPage}
          className="rounded-lg border border-line px-3 py-1.5 text-sm font-semibold text-ink disabled:opacity-40"
        >
          {historyQuery.isFetchingNextPage
            ? t('common:states.loading')
            : t('fleet.history.loadMore')}
        </button>
      )}
    </div>
  );
}
